import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const COUNTIES_GEOJSON_URL =
  "https://raw.githubusercontent.com/plotly/datasets/master/geojson-counties-fips.json";

// Dark coverage states (eastern / central)
const DARK_COVERAGE_STATES = [
  // keep empty or add state FIPS like "36", "34"
];

// Light coverage states (western + others)
const LIGHT_COVERAGE_STATES = [
  "01","12","13","17","18","19","20","21","22","26",
  "27","29","31","38","39","40","42","46","47","48",
  "51","55","54","04","06","08","16","32","35","53"
];

function StateCountyCoverageMap() {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // ---- MAP INIT ----
    const map = L.map(mapRef.current, {
      center: [39, -98],
      zoom: 4.2,
      minZoom: 3,
      maxZoom: 12,
      zoomControl: true,
    });

    mapInstanceRef.current = map;

    // ---- TILE LAYER ----
    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
      { attribution: "Tiles © Esri" }
    ).addTo(map);

    // ---- CUSTOM PANE (ensures borders stay on top) ----
    map.createPane("countyPane");
    map.getPane("countyPane").style.zIndex = 650;

    // ---- LOAD COUNTY DATA ----
    fetch(COUNTIES_GEOJSON_URL)
      .then((res) => res.json())
      .then((data) => {

        const darkCoverage = {
          type: "FeatureCollection",
          features: data.features.filter((f) =>
            DARK_COVERAGE_STATES.includes(
              f.id?.toString().substring(0, 2)
            )
          ),
        };

        const lightCoverage = {
          type: "FeatureCollection",
          features: data.features.filter((f) =>
            LIGHT_COVERAGE_STATES.includes(
              f.id?.toString().substring(0, 2)
            )
          ),
        };

        // ---- DARK COVERAGE LAYER ----
        const darkLayer = L.geoJSON(darkCoverage, {
          pane: "countyPane",
          style: {
            fillColor: "#3d3d3d",
            fillOpacity: 0.85,
            color: "#000000",   // BLACK BORDER
            weight: 1.4,
            opacity: 1,
            lineCap: "butt",
            lineJoin: "miter",
          },
        }).addTo(map);

        // ---- LIGHT COVERAGE LAYER ----
        const lightLayer = L.geoJSON(lightCoverage, {
          pane: "countyPane",
          style: {
            fillColor: "#6b6b6b",
            fillOpacity: 0.75,
            color: "#000000",   // BLACK BORDER
            weight: 1.2,
            opacity: 1,
            lineCap: "butt",
            lineJoin: "miter",
          },
        }).addTo(map);

        // Keep borders on top
        darkLayer.bringToFront();
        lightLayer.bringToFront();

        setLoading(false);
      })
      .catch(() => setLoading(false));

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  return (
    <div
      style={{
        height: 500,
        position: "relative",
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      <div ref={mapRef} style={{ height: "100%", width: "100%" }} />

      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 16,
            zIndex: 1000,
          }}
        >
          Loading county data…
        </div>
      )}
    </div>
  );
}

export default StateCountyCoverageMap;
